import { useState } from "react";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import { Mail, MapPin, Loader2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20">
      <Seo
        title="Contact | Centennial Business Systems"
        description="Get in touch with Centennial about dealership operating systems, toolkits, forms and CDOS access."
        canonicalPath="/contact"
      />
      <div className="grid lg:grid-cols-2 gap-16">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Contact</span>
          <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tighter mt-3">
            Let's get your store systemized.
          </h1>
          <p className="text-muted-foreground mt-5 text-lg">
            Questions about the products, custom packages, or dealer-group licensing? Reach out.
          </p>
          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary flex items-center justify-center"><Mail className="w-4 h-4 text-primary" /></div>
              <span className="text-sm">support@centennialdealershipsystem.com</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary flex items-center justify-center"><MapPin className="w-4 h-4 text-primary" /></div>
              <span className="text-sm">Serving dealerships nationwide</span>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="border border-border bg-white p-8 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name" testid="contact-name" value={form.name} onChange={set("name")} required />
            <Field label="Email" testid="contact-email" type="email" value={form.email} onChange={set("email")} required />
          </div>
          <Field label="Dealership / Company" testid="contact-company" value={form.company} onChange={set("company")} />
          <div>
            <label className="text-sm font-medium block mb-2">Message</label>
            <textarea required value={form.message} onChange={set("message")} rows={5} data-testid="contact-message"
              className="w-full border border-border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <button type="submit" disabled={loading} data-testid="contact-submit"
            className="w-full bg-primary text-white py-3.5 font-semibold btn-lift disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send message"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-2">{label}</label>
      <input data-testid={testid} {...props}
        className="w-full border border-border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary" />
    </div>
  );
}
